import React from 'react';
import { Instagram, Linkedin, Facebook, Twitter, AtSign, CheckCircle2, XCircle, Loader, Clock, Repeat, CalendarDays, Trash2, Pencil, RotateCcw } from 'lucide-react';

const PLATFORM_ICON = { instagram: Instagram, linkedin: Linkedin, facebook: Facebook, x: Twitter, threads: AtSign };

const STATUS_STYLES = {
  draft: 'bg-[#F3F2F2] text-[#5C5C5C]',
  scheduled: 'bg-[#E0F0FD] text-[#0176D3]',
  publishing: 'bg-[#FFF4E5] text-[#B85C00]',
  published: 'bg-[#EBF3ED] text-[#2E844A]',
  partially_published: 'bg-[#FFF4E5] text-[#B85C00]',
  failed: 'bg-[#FDE7E7] text-[#C23934]',
  cancelled: 'bg-[#F3F2F2] text-[#5C5C5C]',
};

const TARGET_STATUS_ICON = {
  published: CheckCircle2,
  failed: XCircle,
  publishing: Loader,
};

// Full detail for one CrossPost — status, every target's own outcome, what's
// still pending vs done vs failed, recurrence info, and Cancel/Delete. Shared
// by the calendar day-modal and the History list so both show identical detail.
export default function CrossPostDetailCard({ post, onCancel, onDelete, onEdit, onRetry }) {
  const pendingCount = post.targets.filter((t) => ['pending', 'publishing'].includes(t.status)).length;
  const doneCount = post.targets.filter((t) => t.status === 'published').length;
  const failedCount = post.targets.filter((t) => t.status === 'failed').length;

  return (
    <div className="border border-[#E5E5E5] rounded-[4px] p-3 space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-1">
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[post.status] || ''}`}>
          {post.status.replace('_', ' ')}
        </span>
        <span className="text-[11px] text-[#5C5C5C] flex items-center gap-1">
          <Clock size={12} />
          {post.scheduledAt
            ? new Date(post.scheduledAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
            : new Date(post.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      </div>

      {post.caption && <div className="text-[13px] text-[#2E2E2E] whitespace-pre-wrap">{post.caption}</div>}
      {!post.caption && <div className="text-[13px] text-[#5C5C5C] italic">(no caption — media only)</div>}

      {(post.media?.length > 0 ? post.media : post.mediaUrl ? [{ url: post.mediaUrl, mediaType: post.mediaType }] : []).length > 0 && (
        <div className={`grid gap-1 ${(post.media?.length || 1) > 1 ? 'grid-cols-3' : 'grid-cols-1'}`}>
          {(post.media?.length > 0 ? post.media : [{ url: post.mediaUrl, mediaType: post.mediaType }]).map((m, i) => (
            <div key={i} className="relative">
              {m.mediaType === 'video' ? (
                <video src={m.url} className="w-full max-h-40 object-cover rounded-[4px]" controls />
              ) : (
                <img src={m.url} alt="" className="w-full max-h-40 object-cover rounded-[4px]" />
              )}
              {i === 0 && (post.media?.length || 1) > 1 && (
                <span className="absolute bottom-0.5 left-0.5 bg-black/60 text-white text-[9px] font-bold px-1 rounded">1st</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 text-[11px] text-[#5C5C5C]">
        <span>{doneCount} done</span>
        <span>{pendingCount} pending</span>
        {failedCount > 0 && <span className="text-[#BA0517]">{failedCount} failed</span>}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {post.targets.map((t, i) => {
          const Icon = PLATFORM_ICON[t.platform] || CalendarDays;
          const StatusIcon = TARGET_STATUS_ICON[t.status];
          return (
            <span
              key={i}
              className="flex items-center gap-1 text-[11px] text-[#5C5C5C] bg-[#F3F2F2] px-1.5 py-0.5 rounded"
              title={t.error || ''}
            >
              <Icon size={11} />
              {t.label}
              {StatusIcon && (
                <StatusIcon
                  size={11}
                  className={
                    t.status === 'published' ? 'text-[#2E844A]' : t.status === 'failed' ? 'text-[#BA0517]' : 'animate-spin'
                  }
                />
              )}
            </span>
          );
        })}
      </div>

      {post.targets.some((t) => t.status === 'failed' && t.error) && (
        <div className="space-y-0.5">
          {post.targets
            .filter((t) => t.status === 'failed' && t.error)
            .map((t, i) => (
              <div key={i} className="text-[11px] text-[#BA0517]">
                {t.label}: {t.error}
              </div>
            ))}
        </div>
      )}

      {post.recurrence?.frequency !== 'none' && (
        <div className="flex items-center gap-1 text-[11px] text-[#0176D3]">
          <Repeat size={12} />
          Repeats {post.recurrence.frequency} (every {post.recurrence.interval || 1}) · occurrence #{post.occurrenceIndex}
          {post.recurrence.endType === 'after' && ` of ${post.recurrence.occurrences}`}
          {post.recurrence.endType === 'on' && ` until ${new Date(post.recurrence.endDate).toLocaleDateString()}`}
        </div>
      )}

      <div className="flex items-center gap-3 pt-1 flex-wrap">
        {post.status === 'scheduled' && onEdit && (
          <button
            onClick={() => onEdit(post)}
            className="flex items-center gap-1 text-[12px] font-semibold text-[#0176D3] hover:underline"
          >
            <Pencil size={12} />
            Edit
          </button>
        )}
        {['failed', 'partially_published'].includes(post.status) && post.targets.some((t) => t.status === 'failed') && onRetry && (
          <button
            onClick={() => onRetry(post._id)}
            className="flex items-center gap-1 text-[12px] font-semibold text-[#0176D3] hover:underline"
          >
            <RotateCcw size={12} />
            Retry failed target{post.targets.filter((t) => t.status === 'failed').length === 1 ? '' : 's'}
          </button>
        )}
        {['draft', 'scheduled'].includes(post.status) && onCancel && (
          <button onClick={() => onCancel(post._id)} className="text-[12px] font-semibold text-[#BA0517] hover:underline">
            Cancel {post.recurrence?.frequency !== 'none' ? 'this occurrence & stop series' : 'post'}
          </button>
        )}
        {post.status !== 'publishing' && onDelete && (
          <button
            onClick={() => onDelete(post._id)}
            className="flex items-center gap-1 text-[11px] font-semibold text-[#706E6B] hover:text-[#BA0517] hover:underline"
          >
            <Trash2 size={12} />
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
